import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Clock, Globe, Loader2 } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { READ_VOICES, VoicePreview } from "@/components/VoiceControls";
import { LanguageSelect } from "@/components/LanguageSelect";

const ZONES = (() => {
  try {
    return Intl.supportedValuesOf("timeZone");
  } catch {
    return ["UTC", "Europe/London", "America/New_York", "Asia/Ho_Chi_Minh", "Asia/Tokyo"];
  }
})();

const selectCls = "min-h-[36px] rounded-xl border border-white/10 bg-page px-3 text-xs text-ink outline-none";

const Pill = ({ on, children, ...rest }) => (
  <button
    {...rest}
    aria-pressed={on}
    className={`min-h-[30px] rounded-full border px-3 text-xs transition-colors ${
      on ? "border-brand bg-brand/15 font-semibold text-brand" : "border-white/10 text-ink-soft hover:border-brand/40"
    }`}
  >
    {children}
  </button>
);

const hourLabel = (h) => `${String(h).padStart(2, "0")}:00`;

// Everything about how MiLu reads the day: voice, language, topics, time and reminders.
export const PrefsPanel = ({ languages, voice, onVoice, readLang, onReadLang }) => {
  const [settings, setSettings] = useState(null);
  const [cats, setCats] = useState({ mine: [], all: [] });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/news/settings").then(({ data }) => setSettings(data))
      .catch((e) => console.error("could not load your preferences", e));
    api.get("/news/categories").then(({ data }) => setCats(data))
      .catch((e) => console.error("could not load the topics", e));
  }, []);

  const saveSettings = async (change) => {
    const next = { ...settings, ...change };
    setSettings(next);
    setSaving(true);
    try {
      await api.put("/news/settings", {
        digest_hour: next.digest_hour,
        digest_size: next.digest_size,
        morning_hour: next.morning_hour,
        weekend_hour: next.weekend_hour ?? null,
        walk_voice: next.walk_voice,
        morning_email: next.morning_email,
        reading_goal: next.reading_goal,
        alert_email: next.alert_email,
        week_digest: next.week_digest,
        voice: next.voice,
        speed: next.speed,
      });
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail) || "Could not save that");
    } finally {
      setSaving(false);
    }
  };

  const saveZone = async (timezone) => {
    setSettings((s) => ({ ...s, timezone }));
    try {
      await api.put("/calendar/settings", { timezone });
      toast.success(timezone ? `Your day now runs on ${timezone.replace(/_/g, " ")}` : "Time zone cleared");
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail) || "Could not save that time zone");
    }
  };

  const toggleTopic = async (key) => {
    const next = cats.mine.includes(key) ? cats.mine.filter((k) => k !== key) : [...cats.mine, key];
    setCats((c) => ({ ...c, mine: next }));
    try {
      const { data } = await api.put("/news/categories", { categories: next });
      setCats((c) => ({ ...c, mine: data.mine }));
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail) || "Could not save those topics");
    }
  };

  if (!settings) {
    return (
      <div className="mt-4 flex items-center gap-2 text-xs text-ink-soft" data-testid="home-prefs-panel">
        <Loader2 className="h-3.5 w-3.5 animate-spin" /> Fetching your preferences…
      </div>
    );
  }

  return (
    <div className="mt-4 space-y-4 rounded-2xl border border-white/10 bg-page p-4" data-testid="home-prefs-panel">
      <div className="flex flex-wrap items-center gap-2" data-testid="prefs-voice">
        <span className="text-[11px] uppercase tracking-widest text-ink-soft">MiLu's voice</span>
        {READ_VOICES.map((v) => (
          <Pill key={v.id} data-testid={`prefs-voice-${v.id}`} on={voice === v.id} onClick={() => onVoice(v.id)}>
            {v.label.split(" · ")[0]}
          </Pill>
        ))}
        <VoicePreview voice={voice} testid="prefs-voice-preview" />
      </div>

      <div className="flex flex-wrap items-center gap-2" data-testid="prefs-language">
        <span className="text-[11px] uppercase tracking-widest text-ink-soft">Read it in</span>
        <LanguageSelect
          languages={languages}
          value={readLang}
          onChange={onReadLang}
          testId="prefs-language-select"
          compact
        />
      </div>

      <div className="flex flex-wrap items-center gap-2" data-testid="prefs-topics">
        <span className="text-[11px] uppercase tracking-widest text-ink-soft">My topics</span>
        {cats.all.map((c) => (
          <Pill key={c.key} data-testid={`prefs-topic-${c.key}`} on={cats.mine.includes(c.key)}
                onClick={() => toggleTopic(c.key)}>
            {c.label}
          </Pill>
        ))}
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <span className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-widest text-ink-soft">
          <Clock className="h-3.5 w-3.5" /> Daily read time
        </span>
        <select
          data-testid="prefs-hour"
          value={settings.digest_hour}
          onChange={(e) => saveSettings({ digest_hour: Number(e.target.value) })}
          className={selectCls}
        >
          {Array.from({ length: 24 }, (_, h) => (
            <option key={h} value={h}>{hourLabel(h)}</option>
          ))}
        </select>

        <span className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-widest text-ink-soft">
          <Globe className="h-3.5 w-3.5" /> Time zone
        </span>
        <select
          data-testid="prefs-timezone"
          value={settings.timezone || ""}
          onChange={(e) => saveZone(e.target.value)}
          className={selectCls}
        >
          <option value="">Not set</option>
          {(settings.timezone && !ZONES.includes(settings.timezone)
            ? [settings.timezone, ...ZONES]
            : ZONES).map((z) => (
            <option key={z} value={z}>{z.replace(/_/g, " ")}</option>
          ))}
        </select>
      </div>

      <div className="flex flex-wrap items-center gap-2" data-testid="prefs-reminders">
        <span className="text-[11px] uppercase tracking-widest text-ink-soft">Reminders</span>
        <Pill data-testid="prefs-morning-email" on={Boolean(settings.morning_email)}
              onClick={() => saveSettings({ morning_email: !settings.morning_email })}>
          Morning email {settings.morning_email ? "on" : "off"}
        </Pill>
        <Pill data-testid="prefs-alert-email" on={Boolean(settings.alert_email)}
              onClick={() => saveSettings({ alert_email: !settings.alert_email })}>
          Breaking alerts {settings.alert_email ? "on" : "off"}
        </Pill>
        <Pill data-testid="prefs-week-digest" on={Boolean(settings.week_digest)}
              onClick={() => saveSettings({ week_digest: !settings.week_digest })}>
          Weekly round-up {settings.week_digest ? "on" : "off"}
        </Pill>
        {saving && <Loader2 className="h-3.5 w-3.5 animate-spin text-ink-soft" />}
      </div>
    </div>
  );
};
